import { type FC, useCallback, useEffect, useMemo, useState } from "react";

import {
  type AndCondition,
  ConditionType,
  type EventCondition,
  type NumberOfCondition,
  type OrCondition,
  type RootCondition,
  type TraitCondition,
  type TraitConfig,
  TraitType,
  type VisualQueryFilter,
} from "@hightouch/lib/query/visual/types";
import {
  Button,
  Column,
  Combobox,
  DownloadIcon,
  FormField,
  Radio,
  RadioGroup,
  Row,
  Spinner,
  Text,
  TextInput,
} from "@hightouch/htui";
import { Controller, useFormContext } from "react-hook-form";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  isRealtimeTrait,
  traitNameValidator,
} from "src/components/traits/utils";
import * as Yup from "yup";

import { CalculationSummary } from "src/components/traits/calculation-summary";
import { PreviewTrait } from "src/components/traits/preview-trait";
import type { InlinePreviewableTraitType } from "src/components/traits/use-preview-trait";
import { TraitConditions } from "src/pages/traits/components/trait-conditions";
import { useFilterableColumnsQuery, useTraitsQuery } from "src/graphql";
import { useHydrateTraitsWithFilterableAudienceColumns } from "src/hooks/use-fetch-traits-with-filterable-audience-columns";
import type { TraitParentModel } from "src/components/audiences/types";
import { ImportConditionsModal } from "./import-conditions-modal";

type AudienceSubset = {
  subset_value: {
    id: string;
  };
};

type Props = {
  parentModel: TraitParentModel;
  editConditions?: boolean;
  subsets?: AudienceSubset[];
  filterByRelationshipId?: string;
  audienceFilter?: VisualQueryFilter;
  hideCalculationMethod?: boolean;
};

export type CalculationMethod = "trait_template" | "static_value";

export const audienceTraitValidationSchema = (
  connectionSourceType: string | undefined,
  existingAliases: string[] = [],
) =>
  Yup.object()
    .shape({
      calculation_method: Yup.string()
        .oneOf(["trait_template", "static_value"])
        .required(),
      name: traitNameValidator(connectionSourceType).test(
        "unique-alias",
        "An audience trait with this name already exists",
        (value) => !value || !existingAliases.includes(value),
      ),
      type: Yup.string().when("calculation_method", {
        is: "trait_template",
        then: Yup.string().required("Type is required"),
        otherwise: Yup.string().nullable(),
      }),
      template_id: Yup.string().when("calculation_method", {
        is: "trait_template",
        then: Yup.string().required("Please select a template"),
        otherwise: Yup.string().nullable(),
      }),
      relationship_id: Yup.number().when("calculation_method", {
        is: "trait_template",
        then: Yup.number().when("type", {
          is: (value) => value === TraitType.Formula,
          then: Yup.number().nullable(),
          else: Yup.number().required("Please select a related model or event"),
        }),
        otherwise: Yup.number().nullable(),
      }),
      conditions: Yup.array(),
      static_value: Yup.string().when("calculation_method", {
        is: "static_value",
        then: Yup.string()
          .required("Value is required")
          .when("static_value_type", {
            is: "number",
            then: Yup.string().test(
              "is-number",
              "Value must be a valid number",
              (value) => value != null && value !== "" && !isNaN(Number(value)),
            ),
          }),
        otherwise: Yup.string().nullable(),
      }),
      static_value_type: Yup.string().when("calculation_method", {
        is: "static_value",
        then: Yup.string()
          .oneOf(["string", "number"])
          .required("Value type is required"),
        otherwise: Yup.string().nullable(),
      }),
    })
    .required();

export type AudienceTraitFormProps = {
  calculation_method: CalculationMethod;
  name: string;
  type: TraitType;
  template_id: string;
  relationship_id: string | undefined; // Formula traits don't have a relationship
  conditions: TraitCondition[];
  static_value: string;
  static_value_type: "string" | "number";
};

export const AudienceTraitForm: FC<Readonly<Props>> = ({
  parentModel,
  editConditions,
  subsets,
  filterByRelationshipId,
  audienceFilter,
  hideCalculationMethod,
}) => {
  const traitTemplatesQuery = useTraitsQuery({
    isTemplate: true,
    archived: false,
    segmentId: parentModel?.id,
  });

  const allTemplates =
    traitTemplatesQuery.data?.listTraitDefinitions.traits ?? [];
  const batchTemplates = allTemplates.filter((t) => !isRealtimeTrait(t));
  const filteredTemplates = filterByRelationshipId
    ? batchTemplates.filter(
        (t) => String(t.relationship?.id) === filterByRelationshipId,
      )
    : batchTemplates;

  const templateOptions = filteredTemplates.map((template) => ({
    label: template.name,
    description: template.description,
    value: template.id,
  }));

  const {
    traitsWithFilterableColumns,
    isLoading: areFilterableColumnsLoading,
  } = useHydrateTraitsWithFilterableAudienceColumns(parentModel.traits);

  const parentModelWithFilterableTraitColumns = useMemo(() => {
    return {
      ...parentModel,
      traits: traitsWithFilterableColumns,
    };
  }, [parentModel, traitsWithFilterableColumns]);

  const { staticValueTraitEnrich } = useFlags();
  const { control, watch, setValue } = useFormContext();

  const calculationMethod: CalculationMethod =
    watch("calculation_method") ?? "trait_template";

  const templateId = watch("template_id");
  const template = traitTemplatesQuery?.data?.listTraitDefinitions.traits.find(
    (template) => template.id === templateId,
  );

  const type = template?.type as TraitType;

  const relationship =
    parentModel?.relationships.find(
      (relationship) =>
        String(relationship.id) === String(template?.relationship?.id),
    ) ??
    parentModel?.relationships.find(
      (relationship) => String(relationship.id) === filterByRelationshipId,
    );

  const modelFilterableColumns = useFilterableColumnsQuery(
    {
      segmentId: relationship?.to_model.id,
    },
    {
      enabled: Boolean(relationship),
      select: (data) => data.segments_by_pk?.filterable_audience_columns,
    },
  );

  const relationshipWithFilterableColumns = useMemo(() => {
    if (!relationship) {
      return null;
    }

    return {
      to_model: {
        id: relationship.to_model.id,
        filterable_audience_columns: modelFilterableColumns.data ?? [],
      },
    };
  }, [relationship, modelFilterableColumns.data]);

  const importableAudienceConditions = useMemo(() => {
    if (!template || !audienceFilter || !template.relationship?.id) return [];
    const targetId = String(template.relationship.id);

    const findImportableConditions = (
      conditions: RootCondition[],
    ): RootCondition[] => {
      const result: RootCondition[] = [];
      for (const c of conditions) {
        if (
          (c.type === ConditionType.NumberOf ||
            c.type === ConditionType.Event) &&
          c.relationshipId != null &&
          String(c.relationshipId) === targetId
        ) {
          result.push(c);
        } else if (
          c.type === ConditionType.And ||
          c.type === ConditionType.Or
        ) {
          const importableChildConditions = findImportableConditions(
            c.conditions as RootCondition[],
          );
          if (importableChildConditions.length > 0) {
            result.push({ ...c, conditions: importableChildConditions });
          }
        }
      }
      return result;
    };

    return findImportableConditions(audienceFilter.conditions);
  }, [template, audienceFilter]);

  // Transforms filtered RootConditions into the shape TraitConditions expects:
  // EventCondition/NumberOfCondition nodes are replaced by their subconditions
  // (spread into the parent), and And/Or groups are preserved recursively.
  const extractTraitConditions = useCallback(
    (conditions: RootCondition[]): TraitCondition[] => {
      return conditions.flatMap((c) => {
        if (
          c.type === ConditionType.Event ||
          c.type === ConditionType.NumberOf
        ) {
          return ((c as EventCondition | NumberOfCondition).subconditions ??
            []) as unknown as TraitCondition[];
        }
        if (c.type === ConditionType.And || c.type === ConditionType.Or) {
          const inner = extractTraitConditions(
            (c as AndCondition | OrCondition).conditions as RootCondition[],
          );
          return [
            { type: c.type, conditions: inner },
          ] as unknown as TraitCondition[];
        }
        return [];
      });
    },
    [],
  );

  const name = watch("name");

  const conditions = watch("conditions");

  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  const rootImportableAudienceConditions = useMemo<TraitCondition[]>(
    () =>
      importableAudienceConditions.flatMap((c) =>
        c.type === ConditionType.And || c.type === ConditionType.Or
          ? extractTraitConditions([c])
          : (((c as EventCondition | NumberOfCondition)
              .subconditions as unknown as TraitCondition[]) ?? []),
      ),
    [importableAudienceConditions, extractTraitConditions],
  );

  useEffect(() => {
    setValue("type", type);
  }, [setValue, type]);

  useEffect(() => {
    setValue("relationship_id", relationship?.id);
  }, [setValue, relationship?.id]);

  return (
    <>
      <Column>
        <Text fontWeight="medium">Sync a trait value to a destination</Text>
        <Text>
          An audience trait allows you to craft a trait value and sync it scoped
          specifically to this audience only.
        </Text>
      </Column>

      {staticValueTraitEnrich && !hideCalculationMethod && (
        <Controller
          control={control}
          name="calculation_method"
          render={({ field }) => (
            <FormField label="Calculation method">
              <RadioGroup {...field}>
                <Radio
                  value="trait_template"
                  label="Trait template"
                  description="Use an existing trait template to compute a value"
                />
                <Radio
                  value="static_value"
                  label="Static value"
                  description="Set a constant value for every member in this audience"
                />
              </RadioGroup>
            </FormField>
          )}
        />
      )}

      {calculationMethod === "trait_template" && (
        <>
          <Column gap={2}>
            <Controller
              control={control}
              name="template_id"
              render={({ field, fieldState }) => (
                <FormField
                  label="Choose a template"
                  error={fieldState.error?.message}
                >
                  <Combobox
                    options={templateOptions}
                    isLoading={traitTemplatesQuery.isLoading}
                    placeholder="Select a template"
                    width="100%"
                    {...field}
                  />
                </FormField>
              )}
            />
            {template && (
              <Column
                borderRadius="6px"
                borderWidth="hairline"
                borderStyle="solid"
                borderColor="base.border"
                p={4}
                gap={4}
              >
                {areFilterableColumnsLoading ? (
                  <Spinner />
                ) : (
                  <CalculationSummary
                    parentModel={parentModelWithFilterableTraitColumns}
                    relatedModel={template?.relationship?.to_model}
                    type={template.type as TraitType}
                    config={template.config as TraitConfig}
                    // Predictive traits don't support templates
                    predictor={undefined}
                  />
                )}
                {editConditions && relationship && (
                  <TraitConditions
                    isAudienceTrait
                    isLoading={areFilterableColumnsLoading}
                    parentModel={parentModelWithFilterableTraitColumns}
                    relationship={relationshipWithFilterableColumns}
                  />
                )}
                {editConditions &&
                  rootImportableAudienceConditions.length > 0 && (
                    <>
                      <Row
                        pt={4}
                        borderTop="1px solid"
                        borderTopColor="base.border"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Button
                          size="md"
                          icon={DownloadIcon}
                          onClick={() => setIsImportModalOpen(true)}
                        >
                          Import audience filters
                        </Button>
                      </Row>
                      <ImportConditionsModal
                        isOpen={isImportModalOpen}
                        onClose={() => setIsImportModalOpen(false)}
                        traitConditions={rootImportableAudienceConditions}
                        onImport={(selected) => {
                          setValue("conditions", selected, {
                            shouldDirty: true,
                          });
                          setIsImportModalOpen(false);
                        }}
                      />
                    </>
                  )}
              </Column>
            )}
            {!template && relationship && editConditions && (
              <Column
                borderRadius="6px"
                borderWidth="hairline"
                borderStyle="solid"
                borderColor="base.border"
                p={4}
                gap={4}
                opacity={0.5}
              >
                <TraitConditions
                  isAudienceTrait
                  isLoading={areFilterableColumnsLoading}
                  parentModel={parentModelWithFilterableTraitColumns}
                  relationship={relationshipWithFilterableColumns}
                />
              </Column>
            )}
          </Column>
        </>
      )}

      {calculationMethod === "static_value" && (
        <>
          <Controller
            control={control}
            name="static_value_type"
            render={({ field, fieldState }) => (
              <FormField label="Value type" error={fieldState.error?.message}>
                <RadioGroup orientation="horizontal" {...field}>
                  <Radio value="string" label="Text" />
                  <Radio value="number" label="Number" />
                </RadioGroup>
              </FormField>
            )}
          />
          <Controller
            control={control}
            name="static_value"
            render={({ field, fieldState }) => (
              <FormField
                label="Value"
                description="eg. SPRING26"
                error={fieldState.error?.message}
              >
                <TextInput
                  placeholder="Enter a value"
                  width="100%"
                  {...field}
                />
              </FormField>
            )}
          />
        </>
      )}

      <Controller
        control={control}
        name="name"
        render={({ field, fieldState }) => (
          <FormField
            label="Name"
            description="This is how the trait will appear when setting up syncs"
            error={fieldState.error?.message}
          >
            <TextInput placeholder="Enter a name" width="100%" {...field} />
          </FormField>
        )}
      />

      {calculationMethod === "trait_template" && parentModel && (
        <PreviewTrait
          isLoading={areFilterableColumnsLoading}
          isDisabled={!parentModel || !template}
          parentModel={parentModelWithFilterableTraitColumns}
          subsets={subsets}
          trait={{
            name,
            type: template?.type as InlinePreviewableTraitType,
            config: template?.config as TraitConfig,
            conditions: conditions ?? [],
            relationshipId: relationship?.id,
          }}
        />
      )}
    </>
  );
};
